import { useState } from "react"
import {BookmarkPlus, Check, Loader2, LogIn} from 'lucide-react'
import { useAuth } from "@/hooks/useAuth"
import { useCalculationHistory, type HistoryPayload } from "@/hooks/useCalculationHistory"

interface SaveHistoryButtonProps {
  payload: HistoryPayload | null
  variant?: "dark" | "light"
}

export default function SaveHistoryButton({ payload, variant = "dark" }: SaveHistoryButtonProps) {
  const { user, isAuthenticated, signIn } = useAuth()
  const { saveHistory, saving } = useCalculationHistory()
  const [saved, setSaved] = useState(false)
  const [message, setMessage] = useState("")

  const isDark = variant === "dark"
  const userId = user?.userId || ""

  const handleSave = async () => {
    if (!payload) return
    setMessage("")
    try {
      let nextUserId = userId
      if (!isAuthenticated) {
        const signedInUser = await signIn()
        nextUserId = signedInUser?.userId || ""
        if (!nextUserId) {
          setMessage("Sign in to save your result.")
          return
        }
      }
      await saveHistory(nextUserId, payload)
      setSaved(true)
      setMessage("Saved to your history")
      window.setTimeout(() => {
        setSaved(false)
        setMessage("")
      }, 2500)
    } catch {
      setMessage("Could not save. Please try again.")
    }
  }

  if (!payload) return null

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={handleSave}
        disabled={saving || saved}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 disabled:opacity-70 ${
          isDark
            ? "bg-white/10 text-white border border-white/15 hover:bg-white/15"
            : "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
        }`}
      >
        {saving ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : saved ? (
          <Check className="w-3.5 h-3.5" />
        ) : isAuthenticated ? (
          <BookmarkPlus className="w-3.5 h-3.5" />
        ) : (
          <LogIn className="w-3.5 h-3.5" />
        )}
        {saving ? "Saving..." : saved ? "Saved" : isAuthenticated ? "Save result" : "Sign in to save"}
      </button>
      {message ? (
        <p className={`text-[10px] ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>{message}</p>
      ) : null}
    </div>
  )
}
